/**
 * Unified Message Operations API
 * 
 * Consolidates duplicate logic from generate.js, regenerate.js, and EditedPrompt.js
 * Handles all message generation operations: new messages, edits, and regenerations
 */

import { getCacheAndFileData } from "../../utils/sparkApi/getCacheandFileData";
import useGenerateApiSignal from "../../store/useGenerateApiSignal";
import useHeaderSelectedAIModel from "../../store/useHeaderSelectedAIModel";
import useSessionIdStore from "../../store/useSessionIdStore";
import ENDPOINTS from "../endpoints";
import { DENIAL_ERROR_MESSAGES } from "../../utils/helpers";
import useStyleSelectionStore from "../../store/styleSelectionStore";
import PromptStore from "../../store/promptStore";

/**
 * Resolve owner ID with consistent fallback logic
 */
export const resolveOwnerId = (space, chat, user) => {
  return space?.owner_id || chat?.owner_id || user?.uid;
};

/**
 * Build request body for generate API
 * 
 * @param {Object} params - Request parameters
 * @param {'new'|'edit'|'regenerate'} params.operation - Type of operation
 * @returns {Object} Request body for generate API
 */
export const buildGenerateRequestBody = ({
  operation,
  modeRef,
  prompt,
  newPrompt = "",
  chatId,
  assetIds = [],
  orgId,
  space,
  chat,
  user,
  modelId,
  promptId,
  answerId = null,
  cacheId,
  fileData,
  recachingValue,
  sessionId,
  adkSessionId = null,
  activeSkills = null,
  webSearchEnabled = true,
  deviceId = null,
  styleSelection = { mode: "profile_default" },
  styleVersionId = null,
  previousPromptId = null,
  parentAnswerId = null,
  turnId = null,
  sourcePromptId = null,
  sourceAnswerId = null,
  modelOverride = false,
}) => {
  const isRegenerate = operation === 'regenerate';
  
  return {
    type: modeRef,
    prompt: prompt,
    chat_uid: chatId,
    ...(assetIds.length > 0 && { asset_ids: assetIds }),
    org_id: orgId,
    uid: resolveOwnerId(space, chat, user),
    regenerate: isRegenerate,
    model_id: modelId,
    model_override: modelOverride,
    recaching: recachingValue || false,
    cache_id: cacheId,
    file_data: fileData,
    prompt_id: promptId,
    ...(answerId && { answer_id: answerId }),
    new_prompt: newPrompt,
    space_id: space?.space_id,
    by: user?.uid,
    session_id: sessionId,
    ...(adkSessionId && { adk_session_id: adkSessionId }),
    ...(webSearchEnabled === false && { web_search_enabled: false }),
    ...(activeSkills?.length > 0 && { active_skills: activeSkills }),
    ...(deviceId && { device_id: deviceId }),
    style_selection: styleSelection,
    ...(styleVersionId && { style_version_id: styleVersionId }),
    ...(previousPromptId && { previous_prompt_id: previousPromptId }),
    ...(parentAnswerId && { parent_answer_id: parentAnswerId }),
    ...(turnId && { turn_id: turnId }),
    ...(sourcePromptId && { source_prompt_id: sourcePromptId }),
    ...(sourceAnswerId && { source_answer_id: sourceAnswerId }),
  };
};

/**
 * Unified generate message API function
 * 
 * Handles new messages, edits, and regenerations with a single implementation
 * 
 * @param {Object} params - API parameters
 * @param {'new'|'edit'|'regenerate'} params.operation - Type of operation
 * @param {string} params.prompt - Message prompt content
 * @param {string} params.newPrompt - New prompt for edits (optional)
 * @param {string} params.chatId - Chat ID
 * @param {string} params.promptId - Prompt/message ID
 * @param {string} params.apiUrl - API base URL
 * @param {Object} params.space - Spectra/space object
 * @param {Object} params.chat - Chat object
 * @param {Object} params.user - User object
 * @param {string} params.orgId - Organization ID
 * @param {string} params.modeRef - Mode reference
 * @param {string} params.modelId - Model ID (optional, uses selected model if not provided)
 * @param {Array} params.assetIds - Ordered canonical attachment IDs
 * @param {Array} params.messages - Messages array for context
 * @param {Object} params.headers - Additional headers (optional)
 * @returns {Promise<ReadableStreamDefaultReader>} Stream reader
 */
export const generateMessageApi = async ({
  operation,
  prompt,
  newPrompt = "",
  chatId,
  promptId,
  apiUrl,
  space,
  chat,
  user,
  orgId,
  modeRef,
  modelId,
  answerId = null,
  assetIds = [],
  messages = [],
  headers = {},
  webSearchEnabled = null,
  activeSkills = [],
  deviceId = null,
  styleSelection = null,
  styleVersionId = null,
  previousPromptId = null,
  parentAnswerId = null,
  turnId = null,
  sourcePromptId = null,
  sourceAnswerId = null,
  modelOverride = false,
}) => {
  // Get cache data with a short timeout — don't block generate on a cold Firestore read.
  // cache_id/file_data are optional; null is safe (backend handles missing cache gracefully).
  // On cold connections the prewarm promise may still be in-flight; we wait up to 200ms
  // before proceeding without it. Subsequent sends will hit the warm cache instantly.
  const cacheData = chatId
    ? await Promise.race([
        getCacheAndFileData(
          chatId,
          space?.owner_id || chat?.owner_id || null,
          space?.space_id || null,
        ),
        new Promise((resolve) => setTimeout(() => resolve(null), 200)),
      ])
    : null;
  const recachingValue = cacheData?.recaching ?? null;

  const cacheId = cacheData?.cache_id ?? null;
  const fileData = cacheData?.file_data ?? "";
  
  // Get session ID
  const sessionId = useSessionIdStore.getState().session_id;
  
  // Get model ID from selected model if not provided
  const effectiveModelId = modelId || 
    useHeaderSelectedAIModel.getState()?.selectedModel?.id || 
    null;
  const effectiveStyleSelection = styleSelection ||
    useStyleSelectionStore.getState().selection ||
    { mode: "profile_default" };
  const effectiveWebSearchEnabled =
    space?.web_search_enabled === false
      ? false
      : (webSearchEnabled ?? PromptStore.getState()?.webSearchEnabled ?? true);
  
  // Get ADK session ID from last message's answer for continuity
  const adkSessionId = messages.length > 0 
    ? messages[messages.length - 1]?.answers?.[messages[messages.length - 1]?.currentIndex ?? 0]?.adk_session_id
    : null;

  // Build request body
  const body = buildGenerateRequestBody({
    operation,
    modeRef,
    prompt,
    newPrompt,
    chatId,
    assetIds,
    orgId,
    space,
    chat,
    user,
    modelId: effectiveModelId,
    promptId,
    answerId,
    cacheId,
    fileData,
    recachingValue,
    sessionId,
    adkSessionId,
    activeSkills,
    webSearchEnabled: effectiveWebSearchEnabled,
    deviceId,
    styleSelection: effectiveStyleSelection,
    styleVersionId,
    previousPromptId,
    parentAnswerId,
    turnId,
    sourcePromptId,
    sourceAnswerId,
    modelOverride,
  });

  // Abort any existing request for this chat, then register the new signal.
  useGenerateApiSignal.getState().signals[chatId]?.signal?.abort();
  const abortController = new AbortController();
  useGenerateApiSignal.getState().addSignal(chatId, { signal: abortController });

  // Default headers for new message operations
  const defaultHeaders = operation === 'new' ? {
    'X-Source-Context': 'spark',
    'X-Agent-Type': null,
    'X-ADK-Enabled': 'true',
  } : {};

  // Make API request
  const response = await fetch(apiUrl + ENDPOINTS.GENERATE_API, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "X-Session-Token": localStorage.getItem("session_token") || "",
      ...defaultHeaders,
      ...headers,
    },
    body: JSON.stringify(body),
    signal: abortController.signal,
  });

  if (!response.ok) {
    let errorCode = null;
    let errorMessage = null;
    try {
      const errBody = await response.json();
      errorCode = errBody.error;
      errorMessage = errBody.message;
    } catch (_) {}
    const err = new Error(
      DENIAL_ERROR_MESSAGES[errorCode] || errorMessage || "Something went wrong. Please try again."
    );
    err.code = errorCode;
    throw err;
  }

  if (!response.body) {
    throw new Error("Readable stream not supported in response.");
  }

  return response.body.getReader();
};
